<template>
	<div class="event-main" :data-event-type="attributes.type">
		<div class="event-main__info-wrapper">
			<div class="event-main__ending">
				<div class="event-main__ending-text">
					{{ translate("event.event_ends_in") }}
				</div>
				<Timer
					:time="eventEnds"
					:iconSize="16"
					:fontSize="13"
					hasIcon
				/>
			</div>
			<AppTextButton
				:type="StatusTypes.INFO"
				:text="translate('ui.info')"
				@click="openInfo"
				:notification="infoBtnNotification"
			/>
		</div>
		<div class="event-main__info-text" v-html="infoText" />
		<div
			class="event-main__info-text-small"
			v-if="
				attributes.type &&
				[
					EventCategory.CHRISTMAS,
					EventCategory.FIREFEST,
					EventCategory.SOLAR,
					EventCategory.HUNTING,
				].includes(attributes.type)
			"
		>
			{{ smallInfoText }}
		</div>
		<div class="event-main__decor">
			<HorizontalDecorDividerFaded />
			<div class="event-main__decor--middle">
				<EventiumProductionBox
					v-for="i in productionBoxCount"
					:key="`prod_box_${i}`"
					:rare="i === 2"
				/>
			</div>
			<HorizontalDecorDividerFaded direction="right" />
		</div>
		<div class="event-main__income">
			<div class="event-main__income-box">
				<RaggedBoxBackground
					slug="event-income-bg"
					:skin="{
						stopColor1: incomeBgColors[0], // rgba(87, 109, 14, 1)
						stopColor2: incomeBgColors[1], //  rgba(142, 175, 31, 1)
					}"
				/>
				<div class="event-main__income-box-content">
					<div>{{ translate("event.current_balance") }}</div>
					<div>
						<span>{{ formatNumber(eventium) }}</span>
						<AppMainIcon
							:iconName="ResourceType.EVENTIUM"
							:iconSize="20"
						/>
					</div>
				</div>
			</div>
			<div class="event-main__income-box-more">
				<div class="event-main__income-box-more-row">
					<div class="event-main__income-box-more-row-left">
						{{ translate("ui.production") }}
					</div>
					<div class="event-main__income-box-more-row-right">
						<span>{{ formatNumber(baseEventiumIncome) }}</span>
						<AppMainIcon
							class="event-main__income-box-more-row-icon"
							:iconName="ResourceType.EVENTIUM"
							:iconSize="16"
						/>
						<span>{{ `/${TICK_DURATION}s` }}</span>
					</div>
				</div>
				<div
					v-if="isCollectableEvent(attributes.type)"
					class="event-main__income-box-more-row"
				>
					<div class="event-main__income-box-more-row-left">
						{{
							translate(
								`event.${attributes.type}_event.items_on_map`,
							).replace(
								"{resource}",
								`${translate(`event.${attributes.type}_event.eventium`)}`,
							)
						}}
					</div>
					<div class="event-main__income-box-more-row-right">
						<span>
							{{ formatNumber(mapClaimablesCoordinates.length) }}
						</span>
						<span class="not-bold">
							{{ `/${formatNumber(collectablesCap)}` }}
						</span>
					</div>
				</div>
				<div
					v-if="
						attributes.type &&
						[
							EventCategory.CHRISTMAS,
							EventCategory.FISHING,
							EventCategory.FIREFEST,
							EventCategory.SOLAR,
							EventCategory.HUNTING,
						].includes(attributes.type)
					"
					class="event-main__income-box-more-row"
				>
					<div class="event-main__income-box-more-row-left">
						{{
							translate(eventBuildingsText).replace(
								"{building}",
								`${translate(`${eventBuilding}.name`)}`,
							)
						}}
					</div>
					<div class="event-main__income-box-more-row-right">
						<span>{{ formatNumber(eventBuildingsCurrent) }}</span>
						<span class="not-bold">{{
							`/${formatNumber(eventBuildingsMax)}`
						}}</span>
					</div>
				</div>
				<div
					v-if="isCollectableEvent(attributes.type)"
					class="event-main__income-box-more-row"
				>
					<div class="event-main__income-box-more-row-left">
						{{
							translate("event.special_resource_gained").replace(
								"{specialresource}",
								`${translate(`event.${attributes.type}_event.special_recourse`)}`,
							)
						}}
					</div>
					<div class="event-main__income-box-more-row-right">
						<span>
							{{ formatNumber(collectedByType[1] ?? 0) }}
						</span>
						<AppMainIcon
							class="event-main__income-box-more-row-icon"
							:iconName="`${attributes.type}-collectable-rare`"
							:iconSize="16"
						/>
					</div>
				</div>
				<div class="event-main__income-box-more-row">
					<div class="event-main__income-box-more-row-left">
						{{ translate("event.total_produced") }}
					</div>
					<div class="event-main__income-box-more-row-right">
						<span>{{ formatNumber(eventiumAccumulated) }}</span>
						<AppMainIcon
							class="event-main__income-box-more-row-icon"
							:iconName="ResourceType.EVENTIUM"
							:iconSize="16"
						/>
					</div>
				</div>
			</div>
		</div>
		<div class="event-main__bottom-text" v-html="bottomText" />
		<div class="event-main__buttons">
			<AppTextButton
				:type="buttonType"
				:text="translate('event.event_upgrades')"
				@click="goToUpgrades"
				:notification="upgradesNotification"
			/>
			<AppTextButton
				:type="StatusTypes.SECONDARY"
				:text="translate('event.event_shop')"
				@click="goToShop"
				:notification="shopNotification"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import EventiumProductionBox from "@/components/Event/EventiumProductionBox.vue";
import HorizontalDecorDividerFaded from "@/components/SVG/HorizontalDecorDividerFaded.vue";
import RaggedBoxBackground from "@/components/SVG/RaggedBoxBackground.vue";
import Timer from "@/components/Timer.vue";
import {
	FIREFEST_BUILDINGS_DEFAULT_LIMIT,
	LAST_SEEN_EVENT_INTRO_ID_KEY,
	LAST_SEEN_EVENT_SHOP_ID_KEY,
	LAST_SEEN_EVENT_UPGRADES_ID_KEY,
	SOLAR_BUILDINGS_DEFAULT_LIMIT,
	TICK_DURATION,
} from "@/config/constants";
import {
	getEventCollectableConfig,
	isCollectableEvent,
	useEventStore,
} from "@/event";
import { FrameName } from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import { EventUpgradeIds } from "@/game/entities/config/upgrades";
import { getUpgradeCurrentReplaceValueById } from "@/game/utils/upgrades";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useTileRepository from "@/hooks/useTileRepository";
import router from "@/router";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import { EventCategory, RouteNames, SkinTypes, StatusTypes } from "@/types/ui";
import {
	convertUnixTimeToRemainingSeconds,
	formatNumber,
} from "@/utils/format";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, ref } from "vue";

interface Props {
	infoBtnNotification?: string;
	upgradesNotification?: string;
	shopNotification?: string;
}

const { infoBtnNotification, upgradesNotification, shopNotification } =
	defineProps<Props>();
const emitter = markRaw(useEmitter());

const eventStore = useEventStore();
const {
	endDate,
	id,
	baseEventiumIncome,
	attributes,
	collectedByType,
	mapClaimablesCoordinates,
} = storeToRefs(eventStore);

const lastSeenEventIntroId = ref<string | null>(
	localStorage.getItem(LAST_SEEN_EVENT_INTRO_ID_KEY),
);
const lastSeenEventUpgradesId = ref(
	localStorage.getItem(LAST_SEEN_EVENT_UPGRADES_ID_KEY),
);

const lastSeenEventShopId = ref(
	localStorage.getItem(LAST_SEEN_EVENT_SHOP_ID_KEY),
);

const resourcesStore = useResourcesStore();
const { eventium, eventiumAccumulated } = storeToRefs(resourcesStore);

const popupStore = usePopupStore();

const buildingStorageStore = useBuildingsStorageStore();
const buildingStorage = storeToRefs(buildingStorageStore);

const tilerepo = markRaw(useTileRepository());

const productionBoxCount = computed((): number =>
	isCollectableEvent(attributes.value.type) ? 2 : 1,
);

const collectablesCap = computed((): number => {
	const eventType = attributes.value.type;
	return isCollectableEvent(eventType)
		? getEventCollectableConfig(eventType).getPickupCap()
		: 0;
});

const buttonType = computed((): StatusTypes => {
	if (
		isCollectableEvent(attributes.value.type) ||
		(attributes.value.type &&
			[EventCategory.FISHING, EventCategory.HUNTING].includes(
				attributes.value.type,
			))
	) {
		return StatusTypes.WARNING;
	}
	return StatusTypes.PRIMARY;
});

const eventEnds = computed((): number =>
	convertUnixTimeToRemainingSeconds((endDate.value?.getTime() ?? 0) / 1_000),
);

const smallInfoText = computed((): string => {
	if (
		attributes.value.type &&
		[EventCategory.FIREFEST, EventCategory.SOLAR].includes(
			attributes.value.type,
		)
	) {
		return translate("event.buildings_limited");
	}
	if (
		attributes.value.type &&
		[EventCategory.HUNTING].includes(attributes.value.type)
	) {
		return translate(
			`event.${attributes.value.type}_event.get_more_buildings_in_milestones`,
		);
	}
	return translate("event.get_more_buildings_in_milestones");
});

const incomeBgColors = computed((): string[] => {
	if (attributes.value.type === EventCategory.EASTER) {
		return ["rgba(87, 109, 14, 1)", "rgba(142, 175, 31, 1)"];
	}
	if (attributes.value.type === EventCategory.CHRISTMAS) {
		return ["rgba(134, 52, 2, 1)", "rgba(246, 200, 41, 1)"];
	}
	if (attributes.value.type === EventCategory.FISHING) {
		return ["rgba(75, 96, 4, 1)", "rgba(142, 175, 31, 1)"];
	}
	if (attributes.value.type === EventCategory.FIREFEST) {
		return ["rgba(134, 52, 2, 1)", "rgba(246, 200, 41, 1)"];
	}
	if (attributes.value.type === EventCategory.SOLAR) {
		return ["rgba(134, 52, 2, 1)", "rgba(246, 200, 41, 1)"];
	}
	if (attributes.value.type === EventCategory.HUNTING) {
		return ["rgba(134, 52, 2, 1)", "rgba(246, 200, 41, 1)"];
	}
	if (attributes.value.type === EventCategory.HALLOWEEN) {
		return ["rgba(226, 90, 0, 1)", "rgba(255, 143, 31, 1)"];
	}
	if (attributes.value.type === EventCategory.PIRATES) {
		return ["rgba(144, 20, 20, 1)", "rgba(224, 33, 33, 1)"];
	}
	return [""];
});

const eventBuilding = computed((): FrameName | null => {
	if (attributes.value.type === EventCategory.CHRISTMAS) {
		return FrameName.DARKWOOD_LUMBER_MILL;
	}

	if (attributes.value.type === EventCategory.FIREFEST) {
		return FrameName.FIRE_FESTIVAL_PYRE;
	}
	if (attributes.value.type === EventCategory.SOLAR) {
		return FrameName.BOUNTIFUL_MARKETPLACE;
	}
	return null;
});

const eventBuildingsText = computed((): string => {
	if (
		attributes.value.type &&
		[EventCategory.FISHING, EventCategory.HUNTING].includes(
			attributes.value.type,
		)
	) {
		return `event.${attributes.value.type}_event.buildings_built`;
	}
	return "event.buildings_built";
});

const eventBuildingsCurrent = computed((): number => {
	if (attributes.value.type === EventCategory.CHRISTMAS) {
		return tilerepo.getAllDarkwoodLumberMills()?.length ?? 0;
	}
	if (attributes.value.type === EventCategory.FISHING) {
		const raftsCount = tilerepo.getAllRafts()?.length ?? 0;
		const bottleTraps = tilerepo.getAllBottleTraps()?.length ?? 0;
		return raftsCount + bottleTraps;
	}
	if (attributes.value.type === EventCategory.FIREFEST) {
		return tilerepo.getTileCount2(
			new Set<FrameName>([FrameName.FIRE_FESTIVAL_PYRE]),
		);
	}
	if (attributes.value.type === EventCategory.SOLAR) {
		return tilerepo.getTileCount2(
			new Set<FrameName>([FrameName.BOUNTIFUL_MARKETPLACE]),
		);
	}
	if (attributes.value.type === EventCategory.HUNTING) {
		const huntingCamps = tilerepo.getAllHuntingCamps()?.length ?? 0;
		const highlandHunters = tilerepo.getAllHighlandHunters()?.length ?? 0;
		return huntingCamps + highlandHunters;
	}
	return 0;
});

const eventBuildingsMax = computed((): number => {
	if (attributes.value.type === EventCategory.FISHING) {
		const raftsStorage = buildingStorage[FrameName.RAFT].value ?? 0;
		const bottleTrapsStorage =
			buildingStorage[FrameName.BOTTLETRAP].value ?? 0;
		return eventBuildingsCurrent.value + raftsStorage + bottleTrapsStorage;
	}
	if (attributes.value.type === EventCategory.HUNTING) {
		const campsStorage =
			buildingStorage[FrameName.HUNTING_CAMP]?.value ?? 0;
		const huntersStorage =
			buildingStorage[FrameName.HIGHLAND_HUNTER]?.value ?? 0;
		return eventBuildingsCurrent.value + campsStorage + huntersStorage;
	}
	if (attributes.value.type === EventCategory.FIREFEST) {
		const upgradesLimit =
			getUpgradeCurrentReplaceValueById(
				EventUpgradeIds.ff_kindling_of_passion,
			) +
			getUpgradeCurrentReplaceValueById(
				EventUpgradeIds.ff_kindling_of_hope,
			);
		return FIREFEST_BUILDINGS_DEFAULT_LIMIT + upgradesLimit;
	}
	if (attributes.value.type === EventCategory.SOLAR) {
		const upgradesLimit =
			getUpgradeCurrentReplaceValueById(
				EventUpgradeIds.sf_free_market_economy,
			) +
			getUpgradeCurrentReplaceValueById(
				EventUpgradeIds.sf_booming_economy,
			) +
			getUpgradeCurrentReplaceValueById(EventUpgradeIds.sf_special_02);
		return SOLAR_BUILDINGS_DEFAULT_LIMIT + upgradesLimit;
	}
	if (!eventBuilding.value) return 0;
	return (
		eventBuildingsCurrent.value +
		(buildingStorage[eventBuilding.value].value ?? 0)
	);
});

const infoText = computed((): string => {
	let text = "event.build_event_buildings_to_produce_resource";
	if (isCollectableEvent(attributes.value.type)) {
		text = `event.${attributes.value.type}_event.collect_from_map`;
	} else if (
		attributes.value.type &&
		[
			EventCategory.FIREFEST,
			EventCategory.SOLAR,
			EventCategory.HUNTING,
		].includes(attributes.value.type)
	) {
		text = `event.${attributes.value.type}_event.build_event_buildings_to_produce_resource`;
	}
	return translate(text)
		.replace(
			"{resource}",
			`<span class="text-icon icon-${attributes.value.type}-eventium-16"> </span> <span> ${translate(`event.${attributes.value.type}_event.eventium`)}</span>`,
		)
		.replace(
			"{building}",
			attributes.value.type &&
				[EventCategory.HUNTING, EventCategory.FISHING].includes(
					attributes.value.type,
				)
				? ""
				: `${translate(`${eventBuilding.value}.name`)}`,
		);
});

const bottomText = computed((): string => {
	const eventType = attributes.value.type;
	const hasEventSpecificText =
		eventType !== null &&
		[
			EventCategory.EASTER,
			EventCategory.FIREFEST,
			EventCategory.SOLAR,
			EventCategory.HALLOWEEN,
			EventCategory.PIRATES,
		].includes(eventType);
	const text = hasEventSpecificText
		? `event.${eventType}_event.event_resource_info`
		: "event.event_resource_info";

	return translate(text).replace(
		/{resource}/g,
		`<span class="bottom-text-icon icon-${attributes.value.type}-eventium-16"></span>`,
	);
});

const goToUpgrades = (): void => {
	if (id.value) {
		localStorage.setItem(LAST_SEEN_EVENT_UPGRADES_ID_KEY, id.value);
		lastSeenEventUpgradesId.value = id.value;
		emitter.emit(EmitterEvents.EventLocalStorageUpdated);
	}
	popupStore.hidePopup();
	router.push({
		name: RouteNames.UPGRADES,
	});
};

const goToShop = (): void => {
	if (id.value) {
		localStorage.setItem(LAST_SEEN_EVENT_SHOP_ID_KEY, id.value);
		lastSeenEventShopId.value = id.value;
		emitter.emit(EmitterEvents.EventLocalStorageUpdated);
	}
	popupStore.hidePopup();
	router.push({
		name: RouteNames.SHOP,
	});
};

const openInfo = (): void => {
	if (id.value) {
		localStorage.setItem(LAST_SEEN_EVENT_INTRO_ID_KEY, id.value);
		lastSeenEventIntroId.value = id.value;
		emitter.emit(EmitterEvents.EventLocalStorageUpdated);
	}

	let popupContent = `<p>${translate("event.milestone_warning")}</p><br><p>${translate("event.event_upgrades_limited")}</p>`;

	if (
		attributes.value.type &&
		[EventCategory.SOLAR, EventCategory.HUNTING].includes(
			attributes.value.type,
		)
	) {
		popupContent = `<p>${translate("event.event_upgrades_limited")}</p>`;
	}

	if (
		attributes.value.type &&
		[EventCategory.HALLOWEEN, EventCategory.PIRATES].includes(
			attributes.value.type,
		)
	) {
		const rarePickupChance = getEventCollectableConfig(
			attributes.value.type as Parameters<
				typeof getEventCollectableConfig
			>[0],
		).getRareChance();
		popupContent = `<p>${translate(`event.${attributes.value.type}_event.special_item_chance`).replace("{percent}", `${Math.round(rarePickupChance * 100)}%`)}</p><br><p>${translate("event.event_upgrades_limited")}</p>`;
	}

	popupStore.showPopup({
		id: PopupDictionary.EVENT_INFO,
		type: PopupType.INFO,
		heading: translate("ui.info"),
		body: popupContent,
		skin: SkinTypes.LIGHT,
		isCentered: true,
		maxHeight: 25,
		width: 22,
		isPriority: true,
	});
};
</script>

<style lang="scss" scoped>
.event-main {
	padding: 1rem;
	padding-bottom: 0;
	width: 100%;
	overflow-x: hidden;
	&__info-wrapper {
		@include flex-row-center;
		gap: 0.625rem;
		:deep(.text-button) {
			width: 4.063rem;
			height: 2.063rem;
		}
		:deep(.text-button--inner--info) {
			box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
			border-width: 0.075rem;
			border-style: solid;
			border-image-source: linear-gradient(to top, #bd9032, #f0d386);
			border-image-slice: 1;
			background-image: linear-gradient(to top, #092f59, #094891);
			.text-button__text-container {
				color: #f2dcb5;
			}
		}
	}
	&__ending {
		display: flex;
		position: relative;
		justify-content: space-around;
		align-items: center;
		gap: 0.5rem;
		box-shadow:
			inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8),
			0 0 0.5rem 0 #cf0032;
		border: 0.063rem solid #cf0032;
		background-color: #401622;
		padding: 0 0.5rem;
		min-width: 12.563rem;
		height: 2.163rem;
		color: #f2dcb5;
		font-weight: 800;
		font-size: 0.813rem;
		&::before {
			position: absolute;
			top: 0;
			left: 0;
			opacity: 1 !important;
			// NOTE: Multiply blending is better for light colors
			mix-blend-mode: multiply !important;
			background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
				repeat center;
			background-size: 60%;
			width: 100%;
			height: 100%;
			content: "";
		}
		&-text {
			margin-bottom: 0.25rem;
			font-weight: 800;
		}
		:deep(.timer) {
			margin-bottom: 0.25rem;
		}
		:deep(.timer__icon) {
			top: 0.25rem;
		}
	}
	&__info-text {
		margin-top: 1rem;
		color: #fff3dd;
		font-weight: 800;
		font-size: 0.906rem;
		line-height: 1rem;
		text-align: center;
		:deep(span) {
			font-weight: 800;
			&.text-icon {
				position: relative;
				top: 0.15rem;
				margin: 0 0.15rem;
			}
		}
		&-small {
			margin-top: 0.5rem;
			color: #fff3dd;
			font-size: 0.688rem;
			text-align: center;
		}
	}
	&__decor {
		margin-top: 1.375rem;
		@include flex-row-center;
		&--middle {
			position: relative;
			flex-shrink: 0;
			margin: 0 0.75rem;
			width: 8.5rem;
			@include flex-row-center;
			gap: 0.25rem;
		}
	}
	&__income {
		position: relative;
		top: 1.375rem;
		width: 100%;
		@include flex-col-center;
		position: relative;
		flex-shrink: 0;
		:deep(.ragged-box-bg) {
			position: absolute;
			width: 100%;
			height: 100%;
		}
		&-box {
			position: relative;
			z-index: 1;
			filter: drop-shadow(0 0 0.125rem rgba(0, 0, 0, 0.4));
			width: 100%;
			height: 2.5rem;
			@include flex-col-center;
			&-content {
				display: flex;
				position: relative;
				justify-content: space-between;
				align-items: center;
				padding: 0 0.625rem;
				width: 100%;
				height: 100%;
				color: #372615;
				& > div {
					margin-bottom: 0.25rem;
					color: #372615;
					font-weight: 800;
					font-size: 0.906rem;
					text-transform: uppercase;
				}
				span {
					margin-right: 0.25rem;
					font-weight: 800;
					font-size: 1.188rem;
				}
				:deep(.icon) {
					position: relative;
					top: 0.15rem;
				}
			}
			&-more {
				position: relative;
				margin-top: -0.5rem;
				box-shadow: inset 0 0 0.8rem 0 rgba(29, 30, 33, 0.8);
				background-image: linear-gradient(to top, #ab8849, #f2dcb5);
				padding: 0.5rem 0.875rem;
				width: 95%;
				color: #372615;
				&::before {
					position: absolute;
					top: 0;
					left: 0;
					opacity: 0.6;
					// NOTE: Color-burn blending is better for dark colors
					mix-blend-mode: color-burn;
					background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
						repeat center;
					background-size: 40%;
					width: 100%;
					height: 100%;
					content: "";
				}
				&-row {
					display: flex;
					justify-content: space-between;
					align-items: center;
					padding: 0.438rem 0;
					&-left {
						margin-right: 0.75rem;
					}
					&:not(:last-child) {
						border-bottom: 0.125rem solid rgba(55, 38, 21, 0.5);
					}
					div {
						display: flex;
						align-items: center;
						color: #372615;
						font-size: 0.875rem;
					}
					span {
						font-weight: 800;
						&.not-bold {
							font-weight: normal;
						}
					}
					&-icon {
						position: relative;
						top: 0.15rem;
						margin: 0 0.15rem;
						&:last-child {
							margin-right: 0;
						}
					}
				}
			}
		}
	}
	&__bottom-text {
		margin-top: 2rem;
		color: #fff3dd;
		font-size: 0.688rem;
		text-align: center;
		:deep(.bottom-text-icon) {
			position: relative;
			top: 0.15rem;
		}
	}
	&__buttons {
		display: flex;
		justify-content: center;
		align-items: center;
		gap: 0.75rem;
		margin-top: 1.5rem;
		margin-bottom: 0.75rem;
		:deep(.text-button) {
			max-width: 11.875rem;
			text-transform: none;
		}
	}
	&[data-event-type="christmas"] {
		.event-main__decor {
			:deep(.eventium-box-background) {
				filter: drop-shadow(0 0 0.3rem #f4d278);
			}
		}
	}
	&[data-event-type="easter"] {
		.event-main__income-box-content {
			& > div {
				color: #f4d278;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
		}
	}
	&[data-event-type="fishing"] {
		.event-main__income-box-content {
			& > div {
				color: #fff3dd;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
		}
	}
	&[data-event-type="pirates"] {
		.event-main__income-box-content {
			& > div {
				color: #fff3dd;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			}
		}
	}
}
</style>
